import { useEffect, useState } from "react";
import { useMemo } from "react";
import styled from "styled-components";
import { AnchorButton, Button } from "@blueprintjs/core";
import {
  faBook,
  faClipboard,
  faClipboardCheck,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Code,
  CodeBlock,
  codeBlockStyle,
  ConfigSetting,
  StyledSwitch,
  SwitchDescription,
  SwitchDiv,
  SwitchSubtitle,
  SwitchTitle,
} from "@components";
import { copyTextToClipboard } from "@utils";

import { getGitLabYAML } from "@pages/setup/utils";

const Warning = styled.div`
  color: var(--global-warning-color);
  visibility: ${({ visible = false }: { visible?: boolean }) =>
    visible ? "visible" : "hidden"};
`;

const Buttons = styled.div`
  display: flex;
  margin-top: var(--global-spacing-l);
  gap: var(--global-spacing-s);
`;

export const GitLabCICode = ({
  withSwitches = false,
  withGitlabSastMention = false,
  deploymentId = undefined,
}: {
  withSwitches?: boolean;
  withGitlabSastMention?: boolean;
  deploymentId?: string;
}) => {
  const [scanMerge, setScanMerge] = useState<boolean>(true);
  const [scanPush, setScanPush] = useState<boolean>(false);
  const [sendResults, setSendResults] = useState<boolean>(false);

  const [copied, setCopied] = useState<boolean>(false);

  const gitlabYaml = useMemo<string>(
    () => getGitLabYAML(scanMerge, scanPush, sendResults, deploymentId),
    [scanMerge, scanPush, sendResults, deploymentId]
  );

  useEffect(() => setCopied(false), [scanMerge, scanPush, sendResults]);

  return (
    <CodeBlock>
      <div style={{ flexGrow: 1 }}>
        <Code
          dark
          copyable
          multiline
          title={<code>.gitlab-ci.yml</code>}
          style={codeBlockStyle}
        >
          {gitlabYaml}
        </Code>
      </div>
      <div>
        <p style={{ inlineSize: "400px" }}>
          Copy this Semgrep CI snippet to your project to scan merge requests
          with two starter security rulesets.
        </p>
        {withSwitches && (
          <>
            <ConfigSetting>
              <SwitchDiv>
                <StyledSwitch
                  checked={scanMerge}
                  onChange={() => setScanMerge(!scanMerge)}
                />
                <SwitchDescription>
                  <SwitchTitle>Scan merge requests</SwitchTitle>
                  <SwitchSubtitle>
                    Scan for new issues only (existing issues ignored)
                  </SwitchSubtitle>
                </SwitchDescription>
              </SwitchDiv>
            </ConfigSetting>
            <ConfigSetting>
              <SwitchDiv>
                <StyledSwitch
                  checked={scanPush}
                  onChange={() => setScanPush(!scanPush)}
                />
                <SwitchDescription>
                  <SwitchTitle>Scan pushes to default branch</SwitchTitle>
                  <SwitchSubtitle>Scan all files for all issues</SwitchSubtitle>
                </SwitchDescription>
              </SwitchDiv>
            </ConfigSetting>
            <ConfigSetting>
              <SwitchDiv>
                <StyledSwitch
                  checked={sendResults}
                  onChange={() => setSendResults(!sendResults)}
                />
                <SwitchDescription>
                  <SwitchTitle>Send results to SAST dashboard</SwitchTitle>
                  <Warning visible={sendResults}>
                    (Note: merge requests will be slower to scan)
                  </Warning>
                </SwitchDescription>
              </SwitchDiv>
            </ConfigSetting>
          </>
        )}
        <Buttons>
          <AnchorButton outlined href="/docs/semgrep-ci">
            <FontAwesomeIcon icon={faBook} /> Semgrep CI Docs
          </AnchorButton>
          <Button
            onClick={async () => {
              await copyTextToClipboard(gitlabYaml);
              setCopied(true);
            }}
            intent="primary"
          >
            {!copied ? (
              <span>
                <FontAwesomeIcon icon={faClipboard} /> Copy snippet
              </span>
            ) : (
              <span>
                <FontAwesomeIcon icon={faClipboardCheck} /> Copied!
              </span>
            )}
          </Button>
        </Buttons>
        {withGitlabSastMention && (
          <p style={{ marginTop: "var(--global-spacing-l)" }}>
            <i>
              Separate from Semgrep CI, GitLab 14+ includes a Semgrep analyzer
              in{" "}
              <a href="https://docs.gitlab.com/ee/user/application_security/sast/index.html#configuration">
                GitLab SAST
              </a>
              , pre-configured for JS,&nbsp;TS,&nbsp;&amp;&nbsp;Python. You can
              run Semgrep CI and GitLab SAST in the same pipeline.
            </i>
          </p>
        )}
      </div>
    </CodeBlock>
  );
};
